import { ArrowFloorButton, FloorButton, SecondaryFloorButton } from '@/components/feature/floor-guide';
import { ChevronDownIcon, ChevronUpIcon, CurrentLocationIcon, PaperIcon } from '@/components/icons';
import { FLOOR_LIST_CONFIG } from '@/constants/floor-guide';
import { useAccessibilityTTS, useDisableWheelEvent, useScreenSize } from '@/hooks';
import { useFloorManagement } from '@/hooks/floor-guide';
import { useConfigStore } from '@/stores';
import type { FloorInfo, LocalizedString } from '@/services/dto';
import { generateAllFloorsGuideWithTTS, generateFloorName, generateFloorNameWithTTS, getFloorDisplayLabel } from '@/utils/floor-guide';
import clsx from 'clsx';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/pagination';
import styles from './styles.module.scss';

const getFloorDisplayName = (floor: FloorInfo, language: string) => floor.name[language] || floor.name.ko || '';

const getFloorCode = (floor: FloorInfo) => (floor as FloorInfo & { floor_code?: string }).floor_code ?? floor.floorCode;

export type BuildingFloorGroup = {
    studioBuildingId: string;
    name: LocalizedString;
    floors: FloorInfo[];
};

type Props = {
    floors?: FloorInfo[];
    buildingFloorGroups?: BuildingFloorGroup[];
    selectedFloorName?: string;
    currentMapBuildingId?: string;
    isShowAllFloorGuide: boolean;
    onSelectFloor?: (floorName: string, floorId: string, buildingId?: string) => void;
    setIsShowAllFloorGuide: (isShow: boolean) => void;
    onShowMyLocation: () => void;
    onShowPoiList?: () => void;
};

const sortFloorsForDisplay = (floors: FloorInfo[]): FloorInfo[] => {
    return [...floors].sort((a, b) => b.order - a.order);
};

export const HorizontalFloorList = ({
    floors,
    buildingFloorGroups,
    selectedFloorName,
    currentMapBuildingId,
    onSelectFloor,
    isShowAllFloorGuide,
    onShowMyLocation,
    onShowPoiList,
}: Props) => {
    const { t, i18n } = useTranslation();
    const { scaleHeightPixel } = useScreenSize();
    const screenMode = useConfigStore((state) => state.config.ui.screenMode);
    const { speak } = useAccessibilityTTS();
    const isFullscreen = screenMode === 'fullscreen';
    const buttonText = t('common.button.tts.button');
    const inactiveButtonText = t('common.button.tts.inactive_button');

    const [lastInteractionWasMouse, setLastInteractionWasMouse] = useState(false);
    const speakCleanupRef = useRef<(() => void) | null>(null);
    const swiperContainerRef = useRef<HTMLDivElement>(null);

    const isMultiBuilding = (buildingFloorGroups?.length ?? 0) > 1;

    const singleBuildingFloors = useMemo(() => {
        if (isMultiBuilding) {
            return [];
        }
        if (buildingFloorGroups?.length === 1) {
            return sortFloorsForDisplay(buildingFloorGroups[0].floors);
        }
        return sortFloorsForDisplay(floors ?? []);
    }, [buildingFloorGroups, floors, isMultiBuilding]);

    const cancelPendingSpeak = useCallback(() => {
        speakCleanupRef.current?.();
        speakCleanupRef.current = null;
    }, []);

    const scheduleSpeak = useCallback(
        (text: string) => {
            cancelPendingSpeak();
            speakCleanupRef.current = speak(text) ?? null;
        },
        [cancelPendingSpeak, speak]
    );

    const handleFloorActionMouseDown = () => {
        setLastInteractionWasMouse(true);
    };

    const handleMyLocationFocus = () => {
        if (!lastInteractionWasMouse) {
            scheduleSpeak(`${t('floor_guide.button.current_location')}, ${buttonText}`);
        }
        setLastInteractionWasMouse(false);
    };

    const handleMyLocationBlur = () => {
        cancelPendingSpeak();
    };

    const handleViewStoreListFocus = () => {
        if (!lastInteractionWasMouse) {
            scheduleSpeak(`${t('floor_guide.button.view_store_list')}, ${buttonText}`);
        }
        setLastInteractionWasMouse(false);
    };

    const [currentActiveIndex, setCurrentActiveIndex] = useState(0);

    const slidesPerView = isFullscreen ? FLOOR_LIST_CONFIG.FULLSCREEN.SLIDES_PER_VIEW : FLOOR_LIST_CONFIG.NORMAL.SLIDES_PER_VIEW;
    const amountToShow = isFullscreen ? FLOOR_LIST_CONFIG.FULLSCREEN.AMOUNT_TO_SHOW : FLOOR_LIST_CONFIG.NORMAL.AMOUNT_TO_SHOW;

    const { handleScrollUp, handleScrollDown, shouldScroll, swiperRef, handleDisableNext, handleDisablePrev, isDisabledPrev, isDisabledNext, floorSlides } =
        useFloorManagement(singleBuildingFloors, amountToShow, selectedFloorName, slidesPerView, currentMapBuildingId);

    const isSelectedItem = useCallback(
        (floor?: FloorInfo) => {
            if (!floor) {
                return false;
            }
            const isSameFloorName = getFloorDisplayName(floor, i18n.language) === selectedFloorName;
            if (!isSameFloorName) {
                return false;
            }
            if (!currentMapBuildingId || !floor.buildingId) {
                return true;
            }
            return floor.buildingId === currentMapBuildingId;
        },
        [selectedFloorName, currentMapBuildingId, i18n.language]
    );

    const getFloorTabIndex = useCallback(
        (slideIndex: number) => {
            if (slideIndex !== currentActiveIndex) return -1;
            return 4;
        },
        [currentActiveIndex]
    );

    const handleSelectFloor = useCallback(
        (floor?: FloorInfo, buildingStudioId?: string) => {
            if (!floor) {
                return;
            }
            const floorName = getFloorDisplayName(floor, i18n.language);
            const floorId = getFloorCode(floor);
            speak(t('floor_guide.text_to_speech.move_to_floor', { floorName: generateFloorName(floorName) }));
            onSelectFloor?.(floorName, floorId, buildingStudioId || floor.buildingId);
        },
        [onSelectFloor, speak, t, i18n.language]
    );

    const handleFocusFloorBtn = useCallback(
        (floor: FloorInfo, selected: boolean) => {
            if (!floor) return;
            const floorName = getFloorDisplayName(floor, i18n.language);
            speak(
                generateFloorNameWithTTS(
                    t,
                    t('floor_guide.text_to_speech.floor', {
                        floorName: generateFloorName(floorName),
                    }),
                    selected
                )
            );
        },
        [speak, t]
    );

    const handleScrollUpWithTTS = useCallback(() => {
        if (isDisabledPrev) {
            speak(t('floor_guide.text_to_speech.click.inactive_prev_floor_btn'));
        } else {
            speak(t('floor_guide.text_to_speech.click.active_prev_floor_btn'));
            handleScrollUp();
        }
    }, [handleScrollUp, isDisabledPrev, speak, t]);

    const handleScrollDownWithTTS = useCallback(() => {
        if (isDisabledNext) {
            speak(t('floor_guide.text_to_speech.click.inactive_next_floor_btn'));
        } else {
            speak(t('floor_guide.text_to_speech.click.active_next_floor_btn'));
            handleScrollDown();
        }
    }, [handleScrollDown, isDisabledNext, speak, t]);

    useDisableWheelEvent(swiperContainerRef);

    const singleBuildingStudioId = buildingFloorGroups?.length === 1 ? buildingFloorGroups[0].studioBuildingId : undefined;

    const renderFloorButton = (floor: FloorInfo, tabIndex: number, buildingStudioId?: string, regionOrder = 4) => (
        <FloorButton
            variant="horizontal"
            data-region-order={regionOrder}
            key={floor.id}
            data-tts-scope="floor-button"
            tabIndex={tabIndex}
            data-interactive
            role="radio"
            aria-checked={isSelectedItem(floor)}
            isSelected={isSelectedItem(floor)}
            aria-label={generateFloorNameWithTTS(
                t,
                t('floor_guide.text_to_speech.floor', {
                    floorName: generateFloorName(getFloorDisplayName(floor, i18n.language)),
                }),
                isSelectedItem(floor)
            )}
            onClick={() => handleSelectFloor(floor, buildingStudioId)}
            onFocus={() => handleFocusFloorBtn(floor, isSelectedItem(floor))}
        >
            {getFloorDisplayLabel(getFloorDisplayName(floor, i18n.language)) || '-'}
        </FloorButton>
    );

    return (
        <section className={styles['floor-wrapper']}>
            <div className={styles['floor__inner']}>
                <section
                    className={clsx(styles['floor__actions'], {
                        [styles['floor__actions--split']]: !isFullscreen,
                    })}
                >
                    <SecondaryFloorButton
                        layout="horizontal"
                        data-region-nav
                        tabIndex={2}
                        data-interactive
                        data-region-order={2}
                        data-tts-scope="my-location-button"
                        aria-label={generateAllFloorsGuideWithTTS(t, t('floor_guide.button.all_floors_guide'), isShowAllFloorGuide)}
                        onMouseDown={handleFloorActionMouseDown}
                        onFocus={handleMyLocationFocus}
                        onBlur={handleMyLocationBlur}
                        onClick={onShowMyLocation}
                        isSelected={isShowAllFloorGuide}
                    >
                        <CurrentLocationIcon />
                        {t('floor_guide.button.current_location')}
                    </SecondaryFloorButton>
                    <SecondaryFloorButton
                        layout="horizontal"
                        role="button"
                        data-region-nav
                        data-region-order={3}
                        data-tts-scope="view-store-list-button"
                        tabIndex={3}
                        variant="gray"
                        onMouseDown={handleFloorActionMouseDown}
                        onFocus={handleViewStoreListFocus}
                        onClick={() => onShowPoiList?.()}
                    >
                        <span className="flex-shrink-0">
                            <PaperIcon />
                        </span>
                        {t('floor_guide.button.view_store_list')}
                    </SecondaryFloorButton>
                </section>

                {isMultiBuilding ? (
                    <section className={styles['floor__columns']} data-region-nav data-region-order={4}>
                        {buildingFloorGroups?.map((group) => {
                            const buildingLabel = group.name[i18n.language] ?? group.name.ko ?? '';
                            const sortedFloors = sortFloorsForDisplay(group.floors);

                            return (
                                <div key={group.studioBuildingId} className={styles['floor__column']}>
                                    <p className={styles['floor__column-label']}>{buildingLabel}</p>
                                    <div
                                        className={clsx(styles['floor__column-floors'], {
                                            [styles['floor__column-floors--fullscreen']]: isFullscreen,
                                            [styles['floor__column-floors--split']]: !isFullscreen,
                                        })}
                                    >
                                        {sortedFloors.map((floor) => renderFloorButton(floor, 4, group.studioBuildingId))}
                                    </div>
                                </div>
                            );
                        })}
                    </section>
                ) : (
                    <section className={styles['floor__list']}>
                        {shouldScroll && (
                            <section className={styles['floor__scroll-btn']}>
                                <ArrowFloorButton
                                    data-tts-scope="arrow-floor-button"
                                    data-type="button"
                                    data-region-nav
                                    data-region-order={4}
                                    tabIndex={4}
                                    data-interactive
                                    className="swiper-prev"
                                    aria-disabled={isDisabledPrev}
                                    onClick={() => handleScrollUpWithTTS()}
                                    onFocus={() => {
                                        if (isDisabledPrev) {
                                            speak(`${t('floor_guide.text_to_speech.focus.inactive_prev_floor_btn')} ${inactiveButtonText}`);
                                        } else {
                                            speak(`${t('floor_guide.text_to_speech.focus.active_prev_floor_btn')} ${buttonText}`);
                                        }
                                    }}
                                >
                                    <ChevronUpIcon />
                                </ArrowFloorButton>
                            </section>
                        )}

                        <div ref={swiperContainerRef} className={styles['floor__list-scroll']}>
                            <Swiper
                                className={clsx(styles['floor__list-container'], 'scrollbar-hide', {
                                    [styles['floor__list-container--scroll']]: shouldScroll,
                                    [styles['floor__list-container--fullscreen']]: shouldScroll && isFullscreen,
                                    [styles['floor__list-container--split']]: shouldScroll && !isFullscreen,
                                })}
                                mousewheel={false}
                                allowTouchMove={true}
                                simulateTouch={false}
                                direction="vertical"
                                spaceBetween={scaleHeightPixel(12)}
                                centerInsufficientSlides
                                navigation={{
                                    prevEl: '.swiper-prev',
                                    nextEl: '.swiper-next',
                                }}
                                slidesPerView={1}
                                loop={false}
                                ref={swiperRef}
                                preventInteractionOnTransition
                                onSlideChange={(swiper) => {
                                    handleDisablePrev(swiper.isBeginning);
                                    handleDisableNext(swiper.isEnd);
                                    setCurrentActiveIndex(swiper.activeIndex);
                                }}
                                onInit={(swiper) => {
                                    handleDisablePrev(swiper.isBeginning);
                                    handleDisableNext(swiper.isEnd);
                                    setCurrentActiveIndex(swiper.activeIndex);
                                }}
                            >
                                {floorSlides?.map((floorSlide, slideIndex) => (
                                    <SwiperSlide key={floorSlide.index} className="!h-auto">
                                        <div
                                            className={styles['floor__single-column']}
                                            data-region-nav={slideIndex === currentActiveIndex}
                                            data-region-order={slideIndex === currentActiveIndex ? 4 : -1}
                                        >
                                            {floorSlide.slideContent.map((floor: FloorInfo) =>
                                                renderFloorButton(floor, getFloorTabIndex(slideIndex), singleBuildingStudioId)
                                            )}
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </div>

                        {shouldScroll && (
                            <section className={styles['floor__scroll-btn']}>
                                <ArrowFloorButton
                                    data-tts-scope="arrow-floor-button"
                                    data-type="button"
                                    data-region-nav
                                    data-region-order={4}
                                    tabIndex={4}
                                    data-interactive
                                    className="swiper-next"
                                    aria-disabled={isDisabledNext}
                                    onClick={() => handleScrollDownWithTTS()}
                                    onFocus={() => {
                                        if (isDisabledNext) {
                                            speak(`${t('floor_guide.text_to_speech.focus.inactive_next_floor_btn')} ${inactiveButtonText}`);
                                        } else {
                                            speak(`${t('floor_guide.text_to_speech.focus.active_next_floor_btn')} ${buttonText}`);
                                        }
                                    }}
                                >
                                    <ChevronDownIcon />
                                </ArrowFloorButton>
                            </section>
                        )}
                    </section>
                )}
            </div>
        </section>
    );
};
